// src/components/sops/SopPeopleGrid.js — sopweb-bdb7afcd (SOPs Library, Seam 2)
// Every active signed-in person in the company, two tick boxes each: Can see / Must read. Ticking Must
// read also ticks Can see; Owner / Admin / Manager show "Always" in Can see. The rules live in
// utils/sopsUi.js (setTick / setAllTicks); the god-mode account is never listed.
import React from 'react';
import { setTick, setAllTicks, allTicked, peopleCountLine } from '../../utils/sopsUi';

const Box = ({ checked, onChange, label }) => (
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-label={label}
        className="h-4 w-4 rounded border-gray-300 accent-[#5F4EC9] cursor-pointer" />
);

export default function SopPeopleGrid({ rows, onChange, disabled }) {
    const staff = rows.filter((r) => !r.always);
    return (
        <div>
            <div className="rounded-lg border overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                    {/* sopweb-bdb7afcd — a SOLID header above the rows while the list scrolls: every header
                        cell has its own background (a sticky header's own background isn't painted reliably
                        with collapsed table borders), the header sits on a higher layer (z-10), and the table
                        uses separate borders so the row lines live on the cells. */}
                    <table className="w-full text-sm border-separate border-spacing-0">
                        <thead className="sticky top-0 z-10 text-left text-xs text-gray-600">
                            <tr>
                                <th className="bg-gray-50 px-3 py-2 font-medium">Person</th>
                                <th className="bg-gray-50 px-2 sm:px-3 py-2 font-medium w-24 sm:w-28">
                                    <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                                        <Box checked={staff.length > 0 && allTicked(staff, 'canSee')} onChange={(v) => onChange(setAllTicks(rows, 'canSee', v))} label="Select all: can see" />
                                        Can see
                                    </label>
                                </th>
                                <th className="bg-gray-50 px-2 sm:px-3 py-2 font-medium w-24 sm:w-28">
                                    <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                                        <Box checked={allTicked(rows, 'mustRead')} onChange={(v) => onChange(setAllTicks(rows, 'mustRead', v))} label="Select all: must read" />
                                        Must read
                                    </label>
                                </th>
                            </tr>
                            <tr><td colSpan={3} className="bg-gray-50 border-b border-gray-200 px-3 pb-1.5 text-[11px] font-normal text-gray-500">The boxes in the headings select everyone.</td></tr>
                        </thead>
                        <tbody className={disabled ? 'opacity-60 pointer-events-none' : ''}>
                            {rows.map((r, i) => (
                                <tr key={r.uid}>
                                    <td className={`${i ? 'border-t border-gray-200' : ''} px-3 py-2`}>
                                        <div className="font-medium text-[#12192D]">{r.name}</div>
                                        <div className="text-xs text-gray-500">{r.role}</div>
                                    </td>
                                    <td className={`${i ? 'border-t border-gray-200' : ''} px-2 sm:px-3 py-2`}>
                                        {r.always ? <span className="text-xs text-gray-500" title="Owners, Admins and Managers can always see every SOP">Always</span>
                                            : <Box checked={r.canSee} onChange={(v) => onChange(setTick(rows, r.uid, 'canSee', v))} label={`${r.name} can see`} />}
                                    </td>
                                    <td className={`${i ? 'border-t border-gray-200' : ''} px-2 sm:px-3 py-2`}>
                                        <Box checked={r.mustRead} onChange={(v) => onChange(setTick(rows, r.uid, 'mustRead', v))} label={`${r.name} must read`} />
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-500">No active people in this company.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
            <p className="mt-2 text-sm font-medium text-[#12192D]" aria-live="polite">{peopleCountLine(rows)}</p>
        </div>
    );
}
